<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ReorderableTable from '@/components/ReorderableTable.vue';
import StandardSelect from '@/components/StandardSelect.vue';
import QuestionAnswerDialog from '@/components/dialogs/QuestionAnswerDialog.vue';
import { Constants } from '@/constants';
import type { Locales, ProjectModuleDto, QuestionAnswerDto } from '@/services/types/api.types';
import { useQuestionAnswersStore } from '@/stores/questionAnswers.store';
import { getMaxLengthText } from '@/helpers/text.helper';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const qaStore = useQuestionAnswersStore();
const { questionAnswerList } = storeToRefs(qaStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

type QuestionAnswerFilter = '' | `status:${number}` | 'visibility:public' | 'visibility:private';

// REFS
const filter = ref<QuestionAnswerFilter>(`status:${Constants.ProjectModule.QuestionAnswer.Status.Pending}`);

// COMPUTED
const rows = computed(() => {
  return questionAnswerList.value.filter((qa) => {
    if (!filter.value) return true;

    if (filter.value.startsWith('status:')) {
      return qa.status.toString() === filter.value.replace('status:', '');
    }

    if (filter.value === 'visibility:public') {
      return qa.status === Constants.ProjectModule.QuestionAnswer.Status.Answered && qa.is_public;
    }

    if (filter.value === 'visibility:private') {
      return qa.status === Constants.ProjectModule.QuestionAnswer.Status.Answered && !qa.is_public;
    }

    return true;
  });
});

const columns = computed(() => [
  {
    name: 'question',
    label: t('global.label.question'),
    field: (row: QuestionAnswerDto) => getMaxLengthText(row.question),
    align: 'left',
  },
  {
    name: 'answer',
    label: t('global.label.answer'),
    field: (row: QuestionAnswerDto) => (row.answer ? getMaxLengthText(stripHtml(row.answer)) : ''),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: QuestionAnswerDto) => getStatusLabel(row.status),
    align: 'left',
  },
  {
    name: 'is_public',
    label: t('project-module.questionAnswer.public'),
    field: (row: QuestionAnswerDto) => (row.is_public ? t('global.yes') : t('global.no')),
    align: 'left',
  },
]);

const filterOptions = computed(() => [
  { label: t('project-module.questionAnswer.filter.all'), value: '' },
  {
    label: t('project-module.questionAnswer.status.pending'),
    value: `status:${Constants.ProjectModule.QuestionAnswer.Status.Pending}`,
  },
  { label: t('project-module.questionAnswer.filter.public'), value: 'visibility:public' },
  { label: t('project-module.questionAnswer.filter.private'), value: 'visibility:private' },
  {
    label: t('project-module.questionAnswer.status.junk'),
    value: `status:${Constants.ProjectModule.QuestionAnswer.Status.Junk}`,
  },
]);

// LIFECYCLE
onMounted(async () => {
  if (props.module.id) {
    await qaStore.fetchQuestionAnswersByModuleId(props.module.id);
  }
});

// FUNCTIONS
const getStatusLabel = (status: number) => {
  switch (status) {
    case Constants.ProjectModule.QuestionAnswer.Status.Pending:
      return t('project-module.questionAnswer.status.pending');
    case Constants.ProjectModule.QuestionAnswer.Status.Answered:
      return t('project-module.questionAnswer.status.answered');
    case Constants.ProjectModule.QuestionAnswer.Status.Junk:
      return t('project-module.questionAnswer.status.junk');
    default:
      return '';
  }
};

const stripHtml = (html: string) => {
  return html
    .replace(/<\/?(p|br|div|li|h[1-6]|tr|td|th|blockquote|pre)[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const handleRowClick = (row: QuestionAnswerDto) => {
  $q.dialog({
    component: QuestionAnswerDialog,
    componentProps: {
      questionAnswer: row,
      languageEnabled: props.languageEnabled,
    },
  }).onOk(async () => {
    if (props.module.id) {
      await qaStore.fetchQuestionAnswersByModuleId(props.module.id);
    }
  });
};
</script>

<template>
  <div class="question-answer-list">
    <reorderable-table
      :rows="rows"
      :columns="columns"
      :title="t('project-module.questionAnswer.title')"
      itemKey="id"
      :disable-add-button="true"
      :disable-orderable="true"
      :disable-delete-button="true"
      @row-click="handleRowClick"
    >
      <template v-slot:filters>
        <standard-select
          name="filter"
          :label="t('project-module.questionAnswer.filter.label')"
          class="filters"
          v-model="filter"
          :options="filterOptions"
          emit-value
          map-options
          outlined
          hide-bottom-space
        />
      </template>
    </reorderable-table>
  </div>
</template>

<style lang="scss">
.question-answer-list {
  .filters {
    padding: 10px 0 0 0;
    width: 200px;
  }
}
</style>
